import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardMedia,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Zoom
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Api from '../../Services/Api';
import ImageEditDialog from './Launcher/components/AdCreator/ImageEditDialog';

const ImageGalleryDialog = ({ open, onClose, pageTitle, pageSlug, pageId }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageEditOpen, setImageEditOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);

  // Fetch images when dialog opens
  useEffect(() => {
    if (open && pageSlug) {
      fetchImages();
    } else {
      // Reset state when dialog closes
      setImages([]);
      setError(null);
    }
  }, [open, pageSlug]);

  const fetchImages = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await Api.getRsocPageImages(pageSlug);
      
      if (response && response.success && response.data && response.data.length > 0) {
        // Transform the data to the expected format
        const transformedImages = response.data.map((item, index) => {
          const filename = item.url.split('/').pop();
          return {
            url: item.url,
            name: filename,
            path: item.url, // Using full URL as path
            size: null // Size not provided by API
          };
        });
        setImages(transformedImages);
      } else {
        setError('No images found for this page');
        setImages([]);
      }
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Failed to load images: ' + (err.message || 'Unknown error'));
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setImageViewerOpen(true);
  };

  const handleCloseImageViewer = () => {
    setImageViewerOpen(false);
    setSelectedImage(null);
  };

  const downloadImage = (imageUrl, imageName) => {
    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = imageName || 'rsoc-image.png';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImageEdit = (image) => {
    setEditingImage(image);
    setImageEditOpen(true);
  };

  const handleImageEdited = (editedImageUrl) => {
    // Refresh the images list to show the newly edited image
    fetchImages();
    setImageEditOpen(false);
    setEditingImage(null);
  };

  const getImageUrl = (imagePath) => {
    // If imagePath is already a full URL, return it as is
    if (imagePath && (imagePath.startsWith('http://') || imagePath.startsWith('https://'))) {
      return imagePath;
    }
    // Otherwise, construct the URL
    const baseUrl = process.env.REACT_APP_API_DOMAIN || '';
    return `${baseUrl}/storage/${imagePath}`;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box>
            <Typography variant="h6" component="div">
              Images for "{pageTitle}"
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Slug: {pageSlug}
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: '300px'
            }}>
              <CircularProgress size={48} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading images...
              </Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : images.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No images available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generate images for this page to see them here
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {images.map((image, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ 
                    position: 'relative',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      cursor: 'pointer'
                    }
                  }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={getImageUrl(image.path)}
                      alt={image.name}
                      onClick={() => handleImageClick(image)}
                      sx={{ 
                        objectFit: 'cover',
                        transition: 'opacity 0.2s ease-in-out',
                        '&:hover': {
                          opacity: 0.9
                        }
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      display: 'flex',
                      gap: 1,
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out',
                      '.MuiCard-root:hover &': {
                        opacity: 1
                      }
                    }}>
                      <Tooltip title="View Full Size">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick(image);
                          }}
                          sx={{ 
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                          }}
                        >
                          <FullscreenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Image with AI">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageEdit(image);
                          }}
                          sx={{ 
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                          }}
                        >
                          <AutoFixHighIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download Image">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadImage(getImageUrl(image.path), image.name);
                          }}
                          sx={{ 
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ p: 1, bgcolor: 'rgba(0,0,0,0.05)' }}>
                      <Typography variant="caption" display="block" noWrap>
                        {image.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {image.size ? `${(image.size / 1024).toFixed(1)} KB` : 'Size unknown'}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {images.length > 0 ? `${images.length} image${images.length !== 1 ? 's' : ''} found` : ''}
          </Typography>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full-size image viewer */}
      <Dialog
        open={imageViewerOpen}
        onClose={handleCloseImageViewer}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            bgcolor: 'rgba(0,0,0,0.9)',
            boxShadow: 'none'
          }
        }}
      >
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '80vh',
          position: 'relative'
        }}>
          <IconButton
            onClick={handleCloseImageViewer}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <Box sx={{ 
              maxWidth: '90%', 
              maxHeight: '90%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={getImageUrl(selectedImage.path)}
                alt={selectedImage.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Edit Dialog */}
      {editingImage && (
        <ImageEditDialog
          open={imageEditOpen}
          onClose={() => setImageEditOpen(false)}
          imageUrl={getImageUrl(editingImage.path)}
          onImageEdited={handleImageEdited}
        />
      )}
    </>
  );
};

export default ImageGalleryDialog;